import { Banner, Checkbox, InvertButton, PrimaryButton } from 'component-library';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../../contexts/AppContext';
import SearchableSelectDropdown from '../../helpers/SearchableSelectDropdown';
import { Oem } from '../../models/ApiModels';
import { GetSystemAsync, UpdateSystem } from '../../services/SystemService';

export type BatchStep = 'select' | 'owner' | 'running' | 'result';

type OwnerDropdownOption = {
  id: string;
  value: string;
};

type BatchReassignFailure = {
  id: string;
  reason: string;
};

export type BatchReassignResult = {
  successIds: string[];
  failed: BatchReassignFailure[];
  skippedIds: string[];
};

export interface UseBatchReassignOwnerArgs {
  oems: Oem[];
  canShowBatchAction: boolean;
  onReassignmentComplete: () => void;
}

export interface BatchReassignOwnerController {
  isBatchMode: boolean;
  batchStep: BatchStep;
  selectedSystemIds: Set<string>;
  selectedOwnerId: string;
  confirmIrreversible: boolean;
  isReassigning: boolean;
  result: BatchReassignResult;
  ownerDropdownOptions: OwnerDropdownOption[];
  selectedOwnerName: string;
  selectedCount: number;
  selectedIdList: string[];
  canModifySelection: boolean;
  enterBatchMode: () => void;
  exitBatchMode: () => void;
  resetBatchState: (closeBatchMode: boolean) => void;
  goToOwnerStep: () => void;
  goToSelectionStep: () => void;
  setOwner: (ownerId: string) => void;
  toggleConfirmIrreversible: () => void;
  toggleSelectedSystem: (systemId: string) => void;
  runBatchReassignment: () => Promise<void>;
}

function createDefaultBatchResult(): BatchReassignResult {
  return {
    successIds: [],
    failed: [],
    skippedIds: [],
  };
}

function getErrorReason(error: any): string {
  const responseMessage = error?.response?.data?.message;
  if (responseMessage && typeof responseMessage === 'string') return responseMessage;
  if (typeof error?.response?.data === 'string') return error.response.data;
  if (error?.message && typeof error.message === 'string') return error.message;
  return 'Unknown error';
}

export function useBatchReassignOwner({
  oems,
  canShowBatchAction,
  onReassignmentComplete,
}: UseBatchReassignOwnerArgs): BatchReassignOwnerController {
  const { currentOem } = useContext(AppContext);
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [batchStep, setBatchStep] = useState<BatchStep>('select');
  const [selectedSystemIds, setSelectedSystemIds] = useState<Set<string>>(new Set());
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [confirmIrreversible, setConfirmIrreversible] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);
  const [result, setResult] = useState<BatchReassignResult>(createDefaultBatchResult());

  const ownerOptions = useMemo(() => {
    return currentOem
      ? [
        { id: currentOem.id, name: currentOem.name },
        ...(oems || []).map((oem: Oem) => ({ id: oem.id, name: oem.name })),
      ]
      : (oems || []).map((oem: Oem) => ({ id: oem.id, name: oem.name }));
  }, [currentOem, oems]);

  const ownerDropdownOptions = useMemo(
    () => ownerOptions.map((owner) => ({ id: owner.id, value: owner.name })),
    [ownerOptions]
  );

  const selectedOwnerName = useMemo(
    () => ownerOptions.find((owner) => owner.id === selectedOwnerId)?.name || '',
    [ownerOptions, selectedOwnerId]
  );

  const selectedCount = selectedSystemIds.size;
  const selectedIdList = useMemo(() => Array.from(selectedSystemIds), [selectedSystemIds]);
  const canModifySelection = isBatchMode && batchStep === 'select' && !isReassigning;

  const resetBatchState = useCallback((closeBatchMode: boolean) => {
    setBatchStep('select');
    setSelectedSystemIds(new Set());
    setSelectedOwnerId('');
    setConfirmIrreversible(false);
    setIsReassigning(false);
    setResult(createDefaultBatchResult());
    if (closeBatchMode) setIsBatchMode(false);
  }, []);

  const enterBatchMode = useCallback(() => {
    resetBatchState(false);
    setIsBatchMode(true);
  }, [resetBatchState]);

  const exitBatchMode = useCallback(() => {
    resetBatchState(true);
  }, [resetBatchState]);

  useEffect(() => {
    if (canShowBatchAction || !isBatchMode) return;
    resetBatchState(true);
  }, [canShowBatchAction, isBatchMode, resetBatchState]);

  const goToOwnerStep = useCallback(() => {
    setBatchStep('owner');
  }, []);

  const goToSelectionStep = useCallback(() => {
    setBatchStep('select');
  }, []);

  const setOwner = useCallback((ownerId: string) => {
    setSelectedOwnerId(ownerId);
  }, []);

  const toggleConfirmIrreversible = useCallback(() => {
    setConfirmIrreversible((prev) => !prev);
  }, []);

  const toggleSelectedSystem = useCallback(
    (systemId: string) => {
      if (!canModifySelection || !systemId) return;
      setSelectedSystemIds((prev) => {
        const updated = new Set(prev);
        if (updated.has(systemId)) updated.delete(systemId);
        else updated.add(systemId);
        return updated;
      });
    },
    [canModifySelection]
  );

  const runBatchReassignment = useCallback(async () => {
    if (!selectedOwnerId || !confirmIrreversible || selectedIdList.length === 0 || isReassigning) return;

    setIsReassigning(true);
    setBatchStep('running');
    const successIds: string[] = [];
    const skippedIds: string[] = [];
    const failed: BatchReassignFailure[] = [];

    for (const id of selectedIdList) {
      try {
        const fullSystem = await GetSystemAsync(id);
        if (!fullSystem?.id) throw new Error('System payload missing id');

        if (fullSystem.oemId === selectedOwnerId) {
          skippedIds.push(id);
          continue;
        }

        const updatedSystem = { ...fullSystem, oemId: selectedOwnerId };
        await UpdateSystem(updatedSystem);
        successIds.push(id);
      } catch (error) {
        failed.push({ id, reason: getErrorReason(error) });
      }
    }

    setResult({ successIds, failed, skippedIds });
    setIsReassigning(false);
    setBatchStep('result');
    setConfirmIrreversible(false);
    onReassignmentComplete();
  }, [confirmIrreversible, isReassigning, onReassignmentComplete, selectedIdList, selectedOwnerId]);

  return {
    isBatchMode,
    batchStep,
    selectedSystemIds,
    selectedOwnerId,
    confirmIrreversible,
    isReassigning,
    result,
    ownerDropdownOptions,
    selectedOwnerName,
    selectedCount,
    selectedIdList,
    canModifySelection,
    enterBatchMode,
    exitBatchMode,
    resetBatchState,
    goToOwnerStep,
    goToSelectionStep,
    setOwner,
    toggleConfirmIrreversible,
    toggleSelectedSystem,
    runBatchReassignment,
  };
}

export function BatchReassignOwnerPanel(props: BatchReassignOwnerController) {
  if (!props.isBatchMode) return null;

  return (
    <div className='border-x border-b border-gray-10 bg-white p-4 flex flex-col gap-4'>
      <Banner
        icon
        warning
        text='Bulk owner reassignment updates systems immediately. This action cannot be undone.'
        color='Super Light Red'
      />
      {props.batchStep === 'select' && (
        <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3'>
          <div className='text-gray-80 prose-paragraphBase'>
            Select systems in the list, then continue. Selected: <strong>{props.selectedCount}</strong>
          </div>
          <div className='flex gap-2 flex-wrap'>
            <InvertButton size='small' label='Cancel' onClick={() => props.exitBatchMode()} />
            <PrimaryButton
              size='small'
              label='Continue'
              onClick={() => props.goToOwnerStep()}
              disabled={props.selectedCount === 0}
            />
          </div>
        </div>
      )}
      {props.batchStep === 'owner' && (
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col lg:flex-row lg:items-end gap-3'>
            <div className='w-full lg:max-w-md'>
              <label className='prose-labelTableColumn text-gray-60 uppercase'>New owner</label>
              <div className='mt-1'>
                <SearchableSelectDropdown
                  optionsData={props.ownerDropdownOptions}
                  placeholder='-Select owner-'
                  searchPlaceholder='Search owner...'
                  onValueChosen={(id?: string) => props.setOwner(id || '')}
                  value={props.selectedOwnerName}
                />
              </div>
            </div>
            <InvertButton label='Back to selection' onClick={() => props.goToSelectionStep()} />
          </div>
          <Banner
            icon
            warning
            text={`You are changing the owner for ${props.selectedCount} systems. Changes are applied immediately and cannot be undone.`}
            color='Super Light Red'
          />
          {props.selectedOwnerName && (
            <p className='prose-paragraphBase text-gray-80'>
              New owner: <strong>{props.selectedOwnerName}</strong>
            </p>
          )}
          <div
            className='flex items-center gap-2 w-fit cursor-pointer'
            onClick={() => props.toggleConfirmIrreversible()}
          >
            <Checkbox active={props.confirmIrreversible} onClick={() => undefined} />
            <span className='prose-paragraphBase text-gray-80'>I understand this action cannot be undone.</span>
          </div>
          <div className='flex flex-col gap-2'>
            <PrimaryButton
              label='Apply reassignment'
              onClick={() => props.runBatchReassignment()}
              disabled={!props.selectedOwnerId || !props.confirmIrreversible || props.isReassigning}
            />
            <p className='prose-paragraphSmaller text-gray-60'>
              Only ownership will be changed; other system fields remain unchanged.
            </p>
          </div>
        </div>
      )}
      {props.batchStep === 'running' && (
        <div className='flex items-center gap-3'>
          <div className='w-5 h-5 border-l-2 border-primary-400 rounded-full animate-spin'></div>
          <p className='prose-paragraphBase text-gray-80'>
            Applying reassignment for {props.selectedIdList.length} systems. Please wait...
          </p>
        </div>
      )}
      {props.batchStep === 'result' && (
        <div className='flex flex-col gap-3'>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-2'>
            <div className='border border-gray-10 bg-white p-3'>
              <p className='prose-labelTableColumn text-gray-60 uppercase'>Updated</p>
              <p className='prose-heading4 text-gray-100'>{props.result.successIds.length}</p>
            </div>
            <div className='border border-gray-10 bg-white p-3'>
              <p className='prose-labelTableColumn text-gray-60 uppercase'>Failed</p>
              <p className='prose-heading4 text-error-100'>{props.result.failed.length}</p>
            </div>
            <div className='border border-gray-10 bg-white p-3'>
              <p className='prose-labelTableColumn text-gray-60 uppercase'>Skipped</p>
              <p className='prose-heading4 text-gray-100'>{props.result.skippedIds.length}</p>
            </div>
          </div>
          {props.result.successIds.length > 0 && (
            <div className='border border-gray-10 bg-white p-3'>
              <p className='prose-labelTableColumn text-gray-60 uppercase mb-1'>Updated system IDs</p>
              <p className='prose-paragraphSmaller text-gray-80 break-all'>
                {props.result.successIds.join(', ')}
              </p>
            </div>
          )}
          {props.result.failed.length > 0 && (
            <div className='border border-error-50 bg-error-20 p-3'>
              <p className='prose-labelTableColumn text-error-100 uppercase mb-1'>Failed systems</p>
              <div className='max-h-32 overflow-y-auto flex flex-col gap-1'>
                {props.result.failed.map((item) => (
                  <p key={item.id} className='prose-paragraphSmaller text-error-100 break-all'>
                    {item.id}: {item.reason}
                  </p>
                ))}
              </div>
            </div>
          )}
          {props.result.skippedIds.length > 0 && (
            <div className='border border-gray-10 bg-white p-3'>
              <p className='prose-labelTableColumn text-gray-60 uppercase mb-1'>Skipped system IDs</p>
              <p className='prose-paragraphSmaller text-gray-80 break-all'>
                {props.result.skippedIds.join(', ')}
              </p>
            </div>
          )}
          <p className='prose-paragraphBase text-gray-80'>
            Successful updates are final. Failed systems were not changed.
          </p>
          <div className='flex gap-2 flex-wrap'>
            <InvertButton size='small' label='Back to systems' onClick={() => props.exitBatchMode()} />
            <PrimaryButton
              size='small'
              label='Start another reassignment'
              onClick={() => props.resetBatchState(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
